/**
 * Avatar Component
 *
 * Unified avatar display for users and agents.
 * Shows avatar image if available, falls back to 2-letter initials.
 *
 * @module components/common/Avatar
 */

import { memo, useState } from 'react';
import { Radio } from 'lucide-react';

/**
 * Avatar colors for different entity types
 */
const TYPE_COLORS = {
  user: 'bg-emerald-600 text-emerald-100',
  agent: 'bg-indigo-600 text-indigo-100',
  system: 'bg-gray-600 text-gray-100'
};

/**
 * Size presets for avatar
 */
const SIZE_CLASSES = {
  xs: 'w-5 h-5 text-[10px]',
  sm: 'w-6 h-6 text-xs',
  md: 'w-8 h-8 text-sm',
  lg: 'w-10 h-10 text-base'
};

/**
 * Status indicator size presets
 */
const STATUS_SIZE_CLASSES = {
  xs: 'w-1.5 h-1.5 -bottom-0 -right-0',
  sm: 'w-2 h-2 -bottom-0.5 -right-0.5',
  md: 'w-2.5 h-2.5 -bottom-0.5 -right-0.5',
  lg: 'w-3 h-3 -bottom-1 -right-1'
};

/**
 * Agent operational status colors
 */
const AGENT_STATUS_COLORS = {
  idle: 'bg-green-500',
  working: 'bg-yellow-500',
  focus: 'bg-red-500',
  in_call: 'bg-purple-500',
  offline: 'bg-gray-500'
};

/**
 * Agent operational status labels
 */
const AGENT_STATUS_LABELS = {
  idle: 'Idle',
  working: 'Working',
  focus: 'Focus Mode',
  in_call: 'In Call',
  offline: 'Offline'
};

/**
 * Get initials from a name (2 letters)
 * @param {string} name - The full name
 * @returns {string} 2-letter initials
 */
function getInitials(name) {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

/**
 * Avatar component for users and agents
 *
 * @param {object} props - Component props
 * @param {string} [props.name] - Display name (used for initials and alt text)
 * @param {string} [props.avatarUrl] - URL to avatar image
 * @param {string} [props.type='user'] - Entity type ('user', 'agent', or 'system')
 * @param {string} [props.size='sm'] - Size preset ('xs', 'sm', 'md', 'lg')
 * @param {string} [props.status] - Agent operational status (idle, working, focus, in_call, offline)
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.showTooltip=true] - Whether to show name as tooltip
 */
const Avatar = memo(function Avatar({
  name,
  avatarUrl,
  type = 'user',
  size = 'sm',
  status,
  className = '',
  showTooltip = true
}) {
  const [imgError, setImgError] = useState(false);
  const colorClass = TYPE_COLORS[type] || TYPE_COLORS.user;
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.sm;
  const statusSizeClass = STATUS_SIZE_CLASSES[size] || STATUS_SIZE_CLASSES.sm;
  const displayName = name || 'Unknown';

  // Icon sizes for system avatar
  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };
  const iconSize = iconSizes[size] || iconSizes.sm;

  // Show image only if avatarUrl exists and hasn't errored
  const showImage = avatarUrl && !imgError;

  return (
    <div className={`relative flex-shrink-0 ${className}`} title={showTooltip ? displayName : undefined}>
      {type === 'system' ? (
        <div
          className={`
            ${sizeClass} bg-gray-700
            rounded-full flex items-center justify-center
          `}
        >
          <Radio className={`${iconSize} text-cyan-400`} />
        </div>
      ) : showImage ? (
        <img
          src={avatarUrl}
          alt={displayName}
          className={`${sizeClass} rounded-full object-cover`}
          onError={() => setImgError(true)}
        />
      ) : (
        <div
          className={`
            ${sizeClass} ${colorClass}
            rounded-full flex items-center justify-center font-medium
          `}
        >
          {getInitials(displayName)}
        </div>
      )}

      {/* Status indicator */}
      {type === 'agent' && status && (
        <div
          className={`
            absolute ${statusSizeClass}
            ${AGENT_STATUS_COLORS[status] || AGENT_STATUS_COLORS.offline}
            rounded-full border-2 border-gray-900
          `}
          title={AGENT_STATUS_LABELS[status] || 'Unknown'}
        />
      )}
    </div>
  );
});

/**
 * Avatar with label - shows avatar and name side by side
 *
 * @param {object} props - Component props
 * @param {string} [props.name] - Display name
 * @param {string} [props.avatarUrl] - URL to avatar image
 * @param {string} [props.type='user'] - Entity type ('user' or 'agent')
 * @param {string} [props.size='md'] - Size preset
 * @param {boolean} [props.showType=false] - Whether to show type label below name
 * @param {string} [props.className] - Additional CSS classes
 */
const AvatarWithLabel = memo(function AvatarWithLabel({
  name,
  avatarUrl,
  type = 'user',
  size = 'md',
  showType = false,
  className = ''
}) {
  const displayName = name || 'Unknown';

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Avatar
        name={name}
        avatarUrl={avatarUrl}
        type={type}
        size={size}
        showTooltip={false}
      />
      <div>
        <div className="text-sm font-medium text-gray-200">{displayName}</div>
        {showType && (
          <div className="text-xs text-gray-500 capitalize">{type}</div>
        )}
      </div>
    </div>
  );
});

export {
  Avatar,
  AvatarWithLabel,
  getInitials,
  TYPE_COLORS,
  SIZE_CLASSES,
  AGENT_STATUS_COLORS,
  AGENT_STATUS_LABELS
};
export default Avatar;
